import { cva } from "class-variance-authority";
import type * as React from "react";

const disabledInteraction =
  "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-disabled:pointer-events-none data-disabled:opacity-50";

const neutralInteraction =
  "hover:bg-interaction-hover active:bg-interaction-active aria-checked:bg-interaction-selected aria-checked:hover:bg-interaction-selected-hover aria-expanded:bg-interaction-selected aria-expanded:hover:bg-interaction-selected-hover aria-pressed:bg-interaction-selected aria-pressed:hover:bg-interaction-selected-hover aria-selected:bg-interaction-selected aria-selected:hover:bg-interaction-selected-hover aria-current:bg-interaction-selected aria-current:hover:bg-interaction-selected-hover data-active:bg-interaction-selected data-active:hover:bg-interaction-selected-hover data-checked:bg-interaction-selected data-checked:hover:bg-interaction-selected-hover data-pressed:bg-interaction-selected data-pressed:hover:bg-interaction-selected-hover data-selected:bg-interaction-selected data-selected:hover:bg-interaction-selected-hover";

const menuItemInteraction =
  "data-highlighted:bg-interaction-hover data-highlighted:text-foreground active:bg-interaction-active data-selected:data-highlighted:bg-interaction-selected-hover";

const destructiveMenuItemInteraction =
  "data-[variant=destructive]:data-highlighted:bg-destructive-hover data-[variant=destructive]:data-highlighted:text-destructive data-[variant=destructive]:active:bg-destructive-active";

// 不透明な塗りを持つ操作要素用。--interaction-*は半透明の前景色なので、塗りを
// 上書きしてライトでは明るく、ダークでは変化なしになる。塗りの濃淡で示す。
const secondaryInteraction =
  "bg-secondary hover:bg-secondary-hover active:bg-secondary-active aria-expanded:bg-secondary-active aria-pressed:bg-secondary-active data-open:bg-secondary-active data-pressed:bg-secondary-active data-selected:bg-secondary-active";

const primaryInteraction =
  "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active aria-expanded:bg-primary-active aria-pressed:bg-primary-active data-open:bg-primary-active data-pressed:bg-primary-active";

const destructiveSubtleInteraction =
  "bg-destructive/10 text-destructive hover:bg-destructive-hover active:bg-destructive-active aria-expanded:bg-destructive-active data-open:bg-destructive-active";

const destructiveSolidInteraction =
  "bg-destructive text-white hover:bg-[color-mix(in_oklch,var(--destructive),black_10%)] active:bg-[color-mix(in_oklch,var(--destructive),black_18%)]";

const interactionVariants = cva(
  `transition-colors duration-150 ease-out-expo motion-reduce:transition-none ${disabledInteraction}`,
  {
    variants: {
      intent: {
        neutral: neutralInteraction,
        secondary: secondaryInteraction,
        primary: primaryInteraction,
        "destructive-subtle": destructiveSubtleInteraction,
        "destructive-solid": destructiveSolidInteraction,
        link: "text-primary underline-offset-4 hover:underline active:text-primary-active",
        // 塗りを持つ面の上に重ねる。background-imageとして重ねるので、下地の
        // 塗り（bg-muted等）を上書きせず、ライト・ダークともに同じ量だけ変化する。
        surface:
          "hover:bg-[image:linear-gradient(var(--interaction-hover),var(--interaction-hover))] active:bg-[image:linear-gradient(var(--interaction-active),var(--interaction-active))] data-selected:bg-[image:linear-gradient(var(--interaction-selected),var(--interaction-selected))]",
      },
    },
    defaultVariants: {
      intent: "neutral",
    },
  },
);

const focusRing = "focus-ring";
const focusRingInset = "focus-ring-inset";
const focusRingClipSafe = "focus-ring-clip-safe";
const invalidInteraction =
  "aria-invalid:border-destructive aria-invalid:bg-destructive/15";

type MenuFocusModalityHandlerProps<T extends HTMLElement> = Pick<
  React.HTMLAttributes<T>,
  "onKeyDownCapture" | "onPointerMoveCapture"
>;

function getMenuFocusModalityHandlers<T extends HTMLElement>({
  onKeyDownCapture,
  onPointerMoveCapture,
}: MenuFocusModalityHandlerProps<T> = {}) {
  return {
    onKeyDownCapture(event: React.KeyboardEvent<T>) {
      event.currentTarget.dataset["focusModality"] = "keyboard";
      onKeyDownCapture?.(event);
    },
    onPointerMoveCapture(event: React.PointerEvent<T>) {
      event.currentTarget.dataset["focusModality"] = "pointer";
      onPointerMoveCapture?.(event);
    },
  };
}

export {
  destructiveMenuItemInteraction,
  focusRing,
  focusRingClipSafe,
  focusRingInset,
  getMenuFocusModalityHandlers,
  interactionVariants,
  invalidInteraction,
  menuItemInteraction,
};
